import { useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { OrderListItem, OrderStatus } from './types';
import { SkeletonRows } from '../skeleton';

const PAGE_SIZE = 50;
const STATUSES: readonly OrderStatus[] = [
  'DRAFT',
  'CONFIRMED',
  'PARTIALLY_FULFILLED',
  'COMPLETED',
  'CANCELLED',
];

/** 订单列表页（S12）：单据号 · 状态 · 需求日期 · 行数 · 创建时间；keyset 分页。 */
export function OrdersListPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const canCreate = useCan(PERMISSIONS.ORDER_CREATE);
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [query, setQuery] = useState('');
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['orders', 'list', status, query],
    queryFn: ({ pageParam }) =>
      apiFetch<{ items: OrderListItem[]; nextCursor: string | null }>(
        `/orders?limit=${PAGE_SIZE}${status ? `&status=${status}` : ''}${
          query.trim() ? `&query=${encodeURIComponent(query.trim())}` : ''
        }${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  const parentRef = useRef<HTMLDivElement>(null);

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.orders')}</h1>
        {canCreate && (
          <Link className="btn btn--primary" to="/orders/new">
            {t('orders.newOrder')}
          </Link>
        )}
      </div>

      <div className="chip-row">
        <button
          type="button"
          className={status === '' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() => setStatus('')}
        >
          {t('orders.filterAll')}
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={status === s ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            onClick={() => setStatus(s)}
          >
            {t(`orders.status${s}`)}
          </button>
        ))}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('orders.searchPlaceholder') ?? ''}
          style={{ marginLeft: 'auto', minWidth: '160px', maxWidth: '260px' }}
        />
      </div>

      {isPending ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p>{t('orders.empty')}</p>
        </div>
      ) : (
        <div className="table" style={{ display: 'block' }}>
          <div
            className="row"
            style={{
              fontWeight: 600,
              padding: 'var(--space-2) 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span style={{ width: '160px' }}>{t('orders.colDoc')}</span>
            <span style={{ width: '160px' }}>{t('orders.colStatus')}</span>
            <span style={{ flex: 2 }}>{t('orders.colCustomer')}</span>
            <span style={{ width: '120px' }}>{t('orders.colRequiredDate')}</span>
            <span style={{ width: '80px' }}>{t('orders.colLines')}</span>
            <span style={{ width: '120px' }}>{t('orders.colCreatedAt')}</span>
          </div>
          <div ref={parentRef} style={{ maxHeight: '640px', overflow: 'auto' }}>
            {items.map((item) => (
              <Link
                key={item.id}
                to={`/orders/${item.id}`}
                className="tree__row"
                style={{ height: '48px', textDecoration: 'none', color: 'inherit' }}
              >
                <span style={{ width: '160px' }}>{item.docNo}</span>
                <span style={{ width: '160px' }}>
                  <StatusBadge status={item.status} />
                </span>
                <span style={{ flex: 2 }}>{item.customerRefText ?? '—'}</span>
                <span style={{ width: '120px' }}>
                  {item.requiredDate ? fmt.format(new Date(item.requiredDate)) : '—'}
                </span>
                <span style={{ width: '80px' }}>{item.lineCount}</span>
                <span style={{ width: '120px' }}>{fmt.format(new Date(item.createdAt))}</span>
              </Link>
            ))}
            {hasNextPage && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
                style={{ margin: 'var(--space-3) 0' }}
              >
                {isFetchingNextPage ? t('app.loading') : t('app.loadMore')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_TONE: Record<OrderStatus, 'default' | 'info' | 'success' | 'danger'> = {
  DRAFT: 'default',
  CONFIRMED: 'info',
  PARTIALLY_FULFILLED: 'info',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

export function StatusBadge({ status }: { readonly status: OrderStatus }): React.JSX.Element {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status];
  return (
    <span className={tone === 'default' ? 'badge' : `badge badge--${tone}`}>
      {t(`orders.status${status}`)}
    </span>
  );
}
